import React, { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, useAnimation, useInView } from 'framer-motion'
import {
  LogIn,
  LogOut,
  Shield,
  Zap,
  Shield as ShieldIcon,
  MinusCircle,
  MoonIcon,
  SunIcon,
  Ticket,
  Gift,
} from 'lucide-react'

import ArabicAnimatedTitle from '../components/AnimatedTitle'
import Button from '../components/Button'
import GlassPanel from '../components/GlassPanel'
import { userApi } from '../services/userApi'
import { useTheme, themeStyles } from '../context/ThemeContext'
import ParticleBackground from '../components/ParticleBackground'
import { getUserRole } from '../utils/auth'

const Home: React.FC = () => {
  const [isAdmin, setIsAdmin] = useState(false)
  const [isMaintainer, setIsMaintainer] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [gameRemaining, setGameRemaining] = useState<number>(0)
  const [freeGameRemaining, setFreeGameRemaining] = useState<number>(0)
  const { theme, toggleTheme } = useTheme()
  const currentTheme = themeStyles[theme]

  const navigate = useNavigate()
  const isAuthenticated = !!localStorage.getItem('token')

  useEffect(() => {
    const checkUserStatus = async () => {
      if (isAuthenticated) {
        try {
          setIsLoading(true)
          // Get user role from JWT token
          const { isAdmin: admin, isMaintainer: maintainer } = getUserRole()
          setIsAdmin(admin)
          setIsMaintainer(maintainer)
          
          // Still need to fetch game counts from backend
          const gameResponse = await userApi.getRemainingGames()
          const freeGameResponse = await userApi.getFreeGames()
          setGameRemaining(gameResponse)
          setFreeGameRemaining(freeGameResponse)
        } catch (err) {
          console.error(err)
          setIsAdmin(false)
          setIsMaintainer(false)
        } finally {
          setIsLoading(false)
        }
      } else {
        setIsLoading(false)
      }
    }
    checkUserStatus()
  }, [isAuthenticated])

  const handleSignOut = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    navigate('/register')
  }

  // Scroll-trigger animations
  const rulesRef = useRef<HTMLDivElement>(null)
  const rulesInView = useInView(rulesRef)
  const rulesControls = useAnimation()

  const powerUpsRef = useRef<HTMLDivElement>(null)
  const powerUpsInView = useInView(powerUpsRef)
  const powerUpsControls = useAnimation()

  useEffect(() => {
    if (rulesInView) {
      rulesControls.start('visible')
    }
    if (powerUpsInView) {
      powerUpsControls.start('visible')
    }
  }, [rulesInView, rulesControls, powerUpsInView, powerUpsControls])

  return (
    <div className={`flex flex-col min-h-screen bg-gradient-to-b ${currentTheme.background}`}>
      {/* Background particles */}
      <ParticleBackground />

      {/* ---------- HEADER ---------- */}
      <header className={`fixed top-0 w-full z-50 ${theme === 'dark' ? 'bg-[#1a1a2e]/80' : 'bg-[#F7F9FB]/80'} backdrop-blur-sm shadow-sm py-4`}>
        <nav className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          {/* Left side: brand name */}
          <Link to="/" className={`${currentTheme.text} font-bold text-3xl hover:underline`}>
            مسبار
          </Link>

          {/* Right side: nav links */}
          <div className="flex items-center space-x-8">
            <a href="#rules" className={`${currentTheme.text} text-2xl hover:underline hover:text-[#4B7BFF]`}>
              قواعد اللعبة
            </a>
            <Link to="/buy-sessions" className={`${currentTheme.text} text-2xl hover:underline hover:text-[#4B7BFF]`}>
              شراء جلسات
            </Link>

            {/* Theme Toggle Button - Icon Only */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full ${theme === 'dark' ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'} transition-colors`}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? <SunIcon className="w-6 h-6" /> : <MoonIcon className="w-6 h-6" />}
            </button>

            {isAuthenticated ? (
              <>
                <Button
                  variant="secondary"
                  onClick={handleSignOut}
                  icon={<LogOut className="w-6 h-6" />}
                  className={`shadow-none text-xl ${theme === 'dark' ? 'bg-white/10 hover:bg-white/20 text-white border-none' : ''}`}
                  size="medium"
                >
                  تسجيل الخروج
                </Button>
                {!isLoading && (isAdmin || isMaintainer) && (
                  <Link to="/admin">
                    <Button 
                      variant="secondary" 
                      icon={<Shield className="w-6 h-6" />} 
                      size="medium"
                      className={`text-xl ${theme === 'dark' ? 'bg-white/10 hover:bg-white/20 text-white border-none' : ''}`}
                    >
                      لوحة الإدارة
                    </Button>
                  </Link>
                )}
              </>
            ) : (
              <Link to="/register">
                <Button 
                  variant="secondary" 
                  icon={<LogIn className="w-6 h-6" />} 
                  size="medium"
                  className={`text-xl ${theme === 'dark' ? 'bg-white/10 hover:bg-white/20 text-white border-none' : ''}`}
                >
                  تسجيل الدخول
                </Button>
              </Link>
            )}
          </div>
        </nav>
      </header>

      {/* ---------- SECTION 1: HERO (CREATIVE STYLE) ---------- */}
      <section
        className="relative flex flex-col items-center justify-center pt-32 pb-20 px-4 md:px-8 overflow-hidden"
        style={{ minHeight: '80vh' }}
      >
        {/* Floating Shapes (example) */}
        <motion.div
          className="absolute top-10 left-1/4 w-16 h-16 bg-[#4B7BFF]/40 rounded-full z-0"
          animate={{
            y: [0, 30, 0],
            x: [0, -15, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute top-1/3 right-1/4 w-20 h-20 bg-[#8E44AD]/30 rounded-full z-0"
          animate={{
            y: [0, -20, 0],
            x: [0, 15, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />

        {/* Wave at the bottom */}
        <div className="absolute bottom-0 left-0 w-full z-0">
          <svg
            viewBox="0 0 1440 320"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full"
          >
            <path
              fill={theme === 'dark' ? '#1a1a2e' : '#F7F9FB'}
              fillOpacity="0.7"
              d="M0,256L40,234.7C80,213,160,171,240,138.7C320,107,400,85,480,101.3C560,117,640,171,720,181.3C800,192,880,160,960,138.7C1040,117,1120,107,1200,122.7C1280,139,1360,181,1400,202.7L1440,224L1440,320L1400,320C1360,320,1280,320,1200,320C1120,320,1040,320,960,320C880,320,800,320,720,320C640,320,560,320,480,320C400,320,320,320,240,320C160,320,80,320,40,320L0,320Z"
            />
          </svg>
        </div>

        {/* Title - Centered */}
        <motion.div
          className="z-10 text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, type: 'spring', stiffness: 90 }}
        >
          <ArabicAnimatedTitle text="مـسـبـار" className="mb-2 text-5xl" />
        </motion.div>

        {/* Play Button - Under Title */}
        <motion.div
          className="z-10 flex flex-col items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <Button
            variant="primary"
            size="large"
            onClick={() => navigate('/team-setup')}
            className="relative overflow-hidden group text-xl"
          >
            {/* Fancy animated overlay */}
            <span className="absolute inset-0 bg-gradient-to-r from-[#4B7BFF] to-[#8E44AD] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out opacity-20" />
            <span className="relative">ابدأ التحدي الآن !</span>
          </Button>

          {isAuthenticated && (
            <motion.div
              className="flex flex-row gap-4 mt-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Paid sessions card */}
              <motion.div 
                className={`${theme === 'dark' ? 'bg-[#1F2A48]/80' : 'bg-white/90'} rounded-2xl p-4 shadow-lg backdrop-blur-sm border ${theme === 'dark' ? 'border-[#4B7BFF]/30' : 'border-[#4B7BFF]/20'} flex items-center gap-3 min-w-[180px]`}
                whileHover={{ y: -5, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-[#4B7BFF]/20' : 'bg-[#4B7BFF]/10'}`}>
                  <Ticket className={`w-6 h-6 ${theme === 'dark' ? 'text-[#4B7BFF]' : 'text-[#4B7BFF]'}`} />
                </div>
                <div className="flex flex-col">
                  <span className={`text-sm ${theme === 'dark' ? 'text-white/70' : 'text-gray-500'}`}>جلسات مدفوعة</span>
                  <span className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>{gameRemaining}</span>
                </div>
              </motion.div>
              
              {/* Free sessions card */}
              <motion.div 
                className={`${theme === 'dark' ? 'bg-[#1F2A48]/80' : 'bg-white/90'} rounded-2xl p-4 shadow-lg backdrop-blur-sm border ${theme === 'dark' ? 'border-[#8E44AD]/30' : 'border-[#8E44AD]/20'} flex items-center gap-3 min-w-[180px]`}
                whileHover={{ y: -5, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-[#8E44AD]/20' : 'bg-[#8E44AD]/10'}`}>
                  <Gift className={`w-6 h-6 ${theme === 'dark' ? 'text-[#8E44AD]' : 'text-[#8E44AD]'}`} />
                </div>
                <div className="flex flex-col">
                  <span className={`text-sm ${theme === 'dark' ? 'text-white/70' : 'text-gray-500'}`}>جلسات مجانية</span>
                  <span className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>{freeGameRemaining}</span>
                </div>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </section>

      {/* ---------- SECTION 2: GAME RULES (قواعد اللعبة) ---------- */}
      <section
        id="rules"
        ref={rulesRef}
        className={`relative py-16 ${theme === 'dark' ? 'bg-[#16213e]/50' : 'bg-[#F7F9FB]'} px-4 md:px-8 overflow-hidden`}
      >
        {/* Wave at the top of this section */}
        <div className="absolute top-0 left-0 w-full z-0">
          <svg
            viewBox="0 0 1440 320"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full -translate-y-full"
          >
            <path
              fill={theme === 'dark' ? '#0f3460' : '#E8ECEF'}
              fillOpacity="0.7"
              d="M0,256L40,213.3C80,171,160,85,240,74.7C320,64,400,128,480,138.7C560,149,640,107,720,117.3C800,128,880,192,960,208C1040,224,1120,192,1200,176C1280,160,1360,160,1400,160L1440,160L1440,0L1400,0C1360,0,1280,0,1200,0C1120,0,1040,0,960,0C880,0,800,0,720,0C640,0,560,0,480,0C400,0,320,0,240,0C160,0,80,0,40,0L0,0Z"
            />
          </svg>
        </div>

        <motion.div
          className="max-w-4xl mx-auto text-center relative z-10"
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: { opacity: 1, y: 0 },
          }}
          initial="hidden"
          animate={rulesControls}
          transition={{ duration: 0.6 }}
        >
          <h2 className={`text-5xl font-bold ${currentTheme.text} mb-6`}>قواعد اللعبة</h2>
          <p className={`${currentTheme.text} text-xl md:text-2xl leading-relaxed mb-10`}>
            اختبر معلوماتك وتنافس مع فريقك عبر أسئلة شيقة في مجالات متعددة! حدد أفضل
            الاستراتيجيات للإجابة قبل انتهاء الوقت، واحرص على استخدام القوى الخاصة
            في اللحظات الحاسمة.
          </p>

          {/* Example GlassPanels to detail the rules or categories */}
          <div className="grid gap-8 md:grid-cols-2">
            <GlassPanel>
              <h3 className={`font-semibold text-3xl ${currentTheme.text} mb-2`}>اختيار المجالات</h3>
              <p className={`${currentTheme.text} text-base leading-relaxed`}>
                كل فريق يختار 3 مجالات ممتعة ومتنوعة يتنافس فيها مع الفريق الآخر.
              </p>
            </GlassPanel>

            <GlassPanel>
              <h3 className={`font-semibold text-3xl ${currentTheme.text} mb-2`}>الوقت والنقاط</h3>
              <p className={`${currentTheme.text} text-base leading-relaxed`}>
                لديك 60 ثانية للإجابة، وإذا لم تجب في الوقت المحدد، يحصل الفريق الآخر
                على فرصة 15 ثانية للإجابة وسرقة النقاط!
              </p>
            </GlassPanel>
          </div>
        </motion.div>
      </section>

      {/* ---------- SECTION 3: POWER-UPS (القوى الخاصة) ---------- */}
      <section
        ref={powerUpsRef}
        className="relative py-16 px-4 md:px-8 overflow-hidden"
        style={{ backgroundColor: theme === 'dark' ? '#0f3460' : '#D6E4FF' }}
      >
        {/* Wave at the top of this section */}
        <div className="absolute top-0 left-0 w-full z-0">
          <svg
            viewBox="0 0 1440 320"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full -translate-y-full"
          >
            <path
              fill={theme === 'dark' ? '#16213e' : '#F7F9FB'}
              fillOpacity="0.7"
              d="M0,256L40,213.3C80,171,160,85,240,74.7C320,64,400,128,480,138.7C560,149,640,107,720,117.3C800,128,880,192,960,208C1040,224,1120,192,1200,176C1280,160,1360,160,1400,160L1440,160L1440,0L1400,0C1360,0,1280,0,1200,0C1120,0,1040,0,960,0C880,0,800,0,720,0C640,0,560,0,480,0C400,0,320,0,240,0C160,0,80,0,40,0L0,0Z"
            />
          </svg>
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.h2
            className={`text-5xl font-bold ${currentTheme.text} mb-8`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            القوى الخاصة
          </motion.h2>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-3 gap-8"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {/* 1) مضاعفة النقاط */}
            <GlassPanel className="p-6">
              <Zap className={`${theme === 'dark' ? 'text-[#9B59B6]' : 'text-[#8E44AD]'} w-12 h-12 mb-3 mx-auto`} />
              <h3 className={`text-3xl font-bold ${currentTheme.text} mb-2`}>مضاعفة النقاط</h3>
              <p className={`text-base ${currentTheme.text} leading-relaxed`}>
                ضاعف نقاط السؤال أو اخسرها بالكامل إذا لم تجب.
              </p>
            </GlassPanel>

            {/* 2) خصم نصف النقاط */}
            <GlassPanel className="p-6">
              <ShieldIcon className={`${theme === 'dark' ? 'text-[#9B59B6]' : 'text-[#8E44AD]'} w-12 h-12 mb-3 mx-auto`} />
              <h3 className={`text-3xl font-bold ${currentTheme.text} mb-2`}>خصم نصف النقاط</h3>
              <p className={`text-base ${currentTheme.text} leading-relaxed`}>
                قلل نقاط الفريق المنافس للنصف عند إجابته على سؤال بنجاح.
              </p>
            </GlassPanel>

            {/* 3) منع الجواب */}
            <GlassPanel className="p-6">
              <MinusCircle className={`${theme === 'dark' ? 'text-[#9B59B6]' : 'text-[#8E44AD]'} w-12 h-12 mb-3 mx-auto`} />
              <h3 className={`text-3xl font-bold ${currentTheme.text} mb-2`}>منع الجواب</h3>
              <p className={`text-base ${currentTheme.text} leading-relaxed`}>
                امنع الفريق الآخر من الإجابة تماماً على سؤال محدد.
              </p>
            </GlassPanel>
          </motion.div>
        </div>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer
        className={`mt-auto w-full py-6 ${theme === 'dark' ? 'bg-[#1a1a2e]' : 'bg-[#E8ECEF]'}`}
      >
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className={`${currentTheme.text} text-base`}>
            &copy; {new Date().getFullYear()} مسبار. جميع الحقوق محفوظة.
          </p>
          <div className={`mt-2 flex justify-center space-x-4 ${currentTheme.text}`}>
            <Link to="/contact" className="hover:underline text-base">
              تواصل معنا
            </Link>
            <Link to="/terms" className="hover:underline text-base">
              الشروط والأحكام
            </Link>
            <Link to="/privacy" className="hover:underline text-base">
              سياسة الخصوصية
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Home
